import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';

export interface ICheckRegisterReportParams {
    beginningPeriodNo?: string;
    endingPeriodNo?: string;
    checkDate?: string;
    type?: string;
    office?: string;
    void?: string;
    payType?: string;
    tsdNoList?: string;
    sortBy?: string;
    paidOnly?: boolean;
    beginningCheckDate?: string;
    endingCheckDate?: string;
}

interface ICheckRegisterReportDialogProps {
    dialogName: string;
    hideDialog: () => void;
    onGenerateReport?: (params: ICheckRegisterReportParams) => void;
    // Owned by the parent, populated once the parent has actually tried the report call
    // (e.g. no checks matched the filters).
    message?: string;
}

const labelStyle: React.CSSProperties = { whiteSpace: 'nowrap' };

// Mirrors the Check_Report block (opened by the main screen's "Report" button) and its OK
// button (oid 459), which runs CHECKRPT.RDF. Confirmed via the real CheckRpt.xml report
// definition: "type" here is the person-type filter (Contractor/Employee/Sub-contractor)
// against LM.TYPE - the SAME field that's been mislabeled "Check Type" with Check/Direct
// Deposit values everywhere else in this form family. "payType" is the genuine check-vs-
// direct-deposit filter (against CHECK_REGISTER.CHECK_TYPE), a separate field from "type".
// Oracle's :check_report.head1 is just a dynamic label for the DLC Numbers field, not real
// data - dropped in favor of a plain label, matching the same fix made on the Generate dialog.
const CheckRegisterReportDialog = (props: ICheckRegisterReportDialogProps) => {
    const { register, errors, getValues } = useForm();
    const { get } = useFetch();
    const [officeOptions, setOfficeOptions] = useState<Array<{ value: string; label: string }>>([]);

    useEffect(() => {
        const fetchOffices = async () => {
            const result = await get('/v1.0/Payroll/offices');
            if (Array.isArray(result)) {
                setOfficeOptions([{ value: 'ALL', label: 'All Offices' }, ...result.map((o: { code: string; dscr: string }) => ({ value: o.code, label: o.dscr }))]);
            }
        };
        fetchOffices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOk = () => {
        if (props.onGenerateReport) {
            const values = getValues();
            props.onGenerateReport({
                ...values,
                paidOnly: !!values.paidOnly,
            });
        }
    };

    return (
        <Dialog title="Check Report" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '40%' }}>
            <div className="form-row">
                <RHFInputBox
                    label="Beginning Pay Period"
                    labelStyle={labelStyle}
                    name="beginningPeriodNo"
                    type="number"
                    register={register}
                    errors={errors}
                    labelOnLeft={true}
                    spaceLabelContent={true}
                    style={{ width: '100%' }}
                    bodyStyle={{ width: '50%' }}
                />
                <RHFInputBox
                    label="Ending Pay Period"
                    labelStyle={labelStyle}
                    name="endingPeriodNo"
                    type="number"
                    register={register}
                    errors={errors}
                    labelOnLeft={true}
                    spaceLabelContent={true}
                    style={{ width: '100%' }}
                    bodyStyle={{ width: '50%' }}
                />
            </div>
            <RHFInputBox
                label="Check Date"
                labelStyle={labelStyle}
                name="checkDate"
                type="date"
                register={register}
                errors={errors}
                labelOnLeft={true}
                spaceLabelContent={true}
                style={{ width: 'auto' }}
            />
            <RHFSelect
                label="Type"
                labelStyle={labelStyle}
                name="type"
                options={[
                    { value: 'C', label: 'Contractor' },
                    { value: 'E', label: 'Employee' },
                    { value: 'S', label: 'Sub-Contractor' },
                ]}
                placeholder="All"
                register={register}
                errors={errors}
                labelOnLeft={true}
                style={{ width: '100%' }}
                title="Blank = All"
            />
            <RHFSelect label="Office" labelStyle={labelStyle} name="office" options={officeOptions} defaultVal="ALL" register={register} errors={errors} labelOnLeft={true} style={{ width: '100%' }} />
            <RHFSelect
                label="Check Status"
                labelStyle={labelStyle}
                name="void"
                options={[
                    { value: 'ALL', label: 'All' },
                    { value: 'Y', label: 'Void' },
                    { value: 'N', label: 'Not Void' },
                ]}
                defaultVal="ALL"
                register={register}
                errors={errors}
                labelOnLeft={true}
                style={{ width: '100%' }}
            />
            <RHFSelect
                label="Payment Type"
                labelStyle={labelStyle}
                name="payType"
                options={[
                    { value: 'ALL', label: 'All' },
                    { value: 'C', label: 'Paper Check' },
                    { value: 'D', label: 'Direct Deposit' },
                ]}
                defaultVal="ALL"
                register={register}
                errors={errors}
                labelOnLeft={true}
                style={{ width: '100%' }}
            />
            <label style={labelStyle}>DLC Numbers</label>
            <RHFInputBox name="tsdNoList" type="text" register={register} errors={errors} style={{ width: '100%' }} title="Comma-separated. Blank = All" />
            <RHFSelect
                label="Sort By"
                labelStyle={labelStyle}
                name="sortBy"
                options={[
                    { value: 'CheckNo', label: 'Check No.' },
                    { value: 'CheckDate', label: 'Check Date' },
                    { value: 'TsdNo', label: 'DLC No.' },
                    { value: 'PaidTo', label: 'Paid To' },
                ]}
                defaultVal="CheckNo"
                register={register}
                errors={errors}
                labelOnLeft={true}
                style={{ width: '100%' }}
            />
            <RHFCheckbox label="Paid Only?" labelStyle={labelStyle} name="paidOnly" register={register} errors={errors} labelOnLeft={true} defaultChecked={true} />
            <div className="form-row">
                <RHFInputBox
                    label="Beginning Check Date"
                    labelStyle={labelStyle}
                    name="beginningCheckDate"
                    type="date"
                    register={register}
                    errors={errors}
                    labelOnLeft={true}
                    spaceLabelContent={true}
                    style={{ width: '100%' }}
                    bodyStyle={{ width: '50%' }}
                />
                <RHFInputBox
                    label="Ending Check Date"
                    labelStyle={labelStyle}
                    name="endingCheckDate"
                    type="date"
                    register={register}
                    errors={errors}
                    labelOnLeft={true}
                    spaceLabelContent={true}
                    style={{ width: '100%' }}
                    bodyStyle={{ width: '50%' }}
                />
            </div>
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button className="blue-hover-button" style={{ width: '20%', marginRight: '1rem' }} text="OK" onClick={handleOk} />
                <Button className="blue-hover-button" style={{ width: '20%' }} text="Cancel" onClick={() => props.hideDialog()} />
            </div>
            <div style={{ marginTop: '1rem', minHeight: '1.2rem', textAlign: 'center', color: 'red' }}>{props.message}</div>
        </Dialog>
    );
};

export default CheckRegisterReportDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/checkRegister/CheckRegisterReportDialog.tsx